import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Layers, Phone, FileCheck2, UsersRound, Check, Target } from "lucide-react";

const replacements = [
  { category: "Practice Management System", vendors: "Dentrix, Eaglesoft, Open Dental" },
  { category: "Imaging Software & IT Servers", vendors: "Apteryx XVWeb, DEXIS, managed IT" },
  { category: "Communication Suite", vendors: "Weave, Mango Voice, Podium" },
  { category: "Revenue Intelligence", vendors: "Dental Intel, Practice by Numbers" },
  { category: "Hardware", vendors: "Desk phones, payment terminals" },
  { category: "Billing & eClaims", vendors: "Vyne Trellis, DentalXChange, Tesia" },
  { category: "ePrescriptions", vendors: "DoseSpot" },
  { category: "Insurance Verification", vendors: "Zuub, AirPay" },
  { category: "Online Scheduling", vendors: "NexHealth, LocalMed" },
  { category: "Marketing Suite", vendors: "RevenueWell, SolutionReach" },
];

const Bold = ({ children }) => (
  <span className="font-semibold text-slate-900">{children}</span>
);

const frontOfficeTasks = [
  "Answering inbound calls",
  "Scheduling patients",
  "Making outbound calls",
  "Answering patient text messages",
  "Rescheduling appointments",
  "Calling patients to confirm appointments",
  "Calling to fill empty chairs & cut downtime",
  "Calling patients to book hygiene",
  "Tracking who needs reactivation or case acceptance",
  "Maintaining a live tracker of patient activity",
  "Building long-term relationships to convert patients",
  "Preparing treatment plan narratives",
  "Calling patients when pre-auth returns to win acceptance",
  "Calling patients for 5-star Google reviews",
  "Calling patients to complete intake paperwork",
  "Calling patients for insurance info to run benefits checks",
  "Entering paper forms into the patient chart",
  "Reading, sorting & filing every fax to the right patient",
  "Reviewing the AI workforce's output & assigning it to staff",
  "Cleaning up the staff task list",
];

const backOfficeTasks = [
  "Insurance verifications",
  "Calling insurance for detailed benefits",
  "Getting & analyzing fax-backs for benefits",
  "EOB posting",
  "Calculating adjustments",
  "Reconciling insurance payments",
  "Reconciling text-to-pay payments",
  "Reconciling terminal payments",
  "Managing denials",
  "Auto-drafting appeals",
  "Resubmitting to secondary insurance",
  "Resubmitting claims",
  "Producing patient bills & statements",
  "Calling patients to pay their balances",
  "Sending off pre-auths",
  "Writing narratives",
  "Attaching the appropriate images",
  "ERA enrollment with payers",
  "Hunting down paper EOBs",
  "Analyzing revenue cycle health",
  "Finding gaps in collection rate",
  "Spotting where the headroom is to improve",
];

const TaskGrid = ({ tasks, color }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
    {tasks.map((t, i) => (
      <motion.div
        key={t}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.03 + i * 0.025, duration: 0.28 }}
        className="flex items-start gap-2.5 text-[16px] text-slate-700 leading-snug"
      >
        <Check
          className="w-4 h-4 mt-[3px] flex-shrink-0"
          strokeWidth={3}
          style={{ color }}
        />
        <span>{t}</span>
      </motion.div>
    ))}
  </div>
);

const steps = [
  {
    icon: Layers,
    color: "#3b82f6",
    title: "We unify your software",
    blurb: "10+ vendors become one operating system.",
    detailTitle: "Your entire software stack — replaced.",
    goal:
      "Unifying your stack into one system is the foundation that makes our AI agents far more effective. With context from every part of your operation in one place, they can communicate with patients and complete admin work much more intelligently. The biggest blocker to AI agents being effective is siloed context across multiple systems — we remove it.",
  },
  {
    icon: Phone,
    color: "#6366f1",
    title: "We put AI in your front office",
    blurb: "20+ activities, handled.",
    detailTitle: "AI takes over the work your front office does today.",
    goal:
      "Not just to save staff time — to give you unlimited bandwidth. Even if your team is still doing some of this work, with unlimited bandwidth on top of them, you can grow production. The real goal here is to grow your production.",
  },
  {
    icon: FileCheck2,
    color: "#8b5cf6",
    title: "We put AI in your back office",
    blurb: "20+ activities, handled.",
    detailTitle: "AI takes over the work your back office does today.",
    goal:
      "Automate 65–75% of your revenue cycle completely, with the remaining 25–35% mostly handled but with human review. A tighter, more efficient revenue cycle accelerates your cash flow. Revenue cycle is the most time-consuming part of running a dental office — if AI handles 70%+ of it, your staff can focus on patients and growing the practice.",
  },
  {
    icon: UsersRound,
    color: "#d946ef",
    title: "We onboard you — then consult until it works",
    blurb: "A 12-week calibration, not just setup.",
    detailTitle:
      "We don't just onboard you. We provide consulting until our operating system is achieving outcomes for you.",
    goal:
      "Instead of just onboarding, we put enough account management and consulting cadence on your account to tweak every part of the technology until it's driving outcomes — not just set up. We keep going until the automation actually delivers results for your practice.",
    body: (
      <>
        <p>
          First, a <Bold>white-glove onboarding</Bold>: we configure Asha around how your office
          actually runs — your fee schedules, insurance plans, recall cadences, scheduling rules, and
          clinical workflows — then migrate your data, port your phone lines, and train your team role
          by role so day one just works.
        </p>
        <p>
          But onboarding is only half of it. Every partnership includes a <Bold>consulting
          package</Bold>: a <Bold>DSO-experienced account manager</Bold> works with you not just to
          stand up the software, but to make sure it's effective at hitting the outcomes you need —
          reviewing results, tuning the AI, and adjusting workflows as you go.
        </p>
        <p>
          That <Bold>calibration period runs a full 12 weeks</Bold> — even if setup is finished by
          week two or three. We keep tuning until the automation delivers. Efficacy is our job, not
          yours.
        </p>
      </>
    ),
  },
];

const ReplacementTable = ({ color }) => (
  <div className="rounded-xl border border-slate-200 overflow-hidden">
    <table className="w-full border-collapse">
      <thead>
        <tr className="bg-slate-50">
          {["Software we replace", "Who you're paying today", "With Asha"].map((h, i) => (
            <th
              key={h}
              className={`text-left text-[13px] font-bold uppercase tracking-wider text-slate-400 px-4 py-3 border-b border-slate-200 ${
                i > 0 ? "border-l border-slate-100" : ""
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {replacements.map((r, i) => (
          <motion.tr
            key={r.category}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.05 + i * 0.03, duration: 0.25 }}
            className={i % 2 === 1 ? "bg-slate-50/50" : "bg-white"}
          >
            <td className="px-4 py-2.5 text-[15.5px] font-semibold text-slate-800 border-b border-slate-100 whitespace-nowrap">
              {r.category}
            </td>
            <td className="px-4 py-2.5 text-[15.5px] text-slate-500 border-b border-slate-100 border-l border-l-slate-100 w-full">
              {r.vendors}
            </td>
            <td className="px-4 py-2.5 border-b border-slate-100 border-l border-l-slate-100">
              <span
                className="inline-flex items-center gap-1 text-[14px] font-bold whitespace-nowrap"
                style={{ color }}
              >
                <Check className="w-4 h-4" strokeWidth={3} />
                Included
              </span>
            </td>
          </motion.tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PartnershipSlide = () => {
  const [active, setActive] = useState(0);
  const step = steps[active];

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1280px] w-full mx-auto flex flex-col h-full relative z-10">
        {/* Title */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-[2.6rem] font-extrabold text-slate-900 tracking-tight leading-[1.08]"
          >
            How we partner with{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
              your office.
            </span>
          </motion.h2>
        </div>

        {/* Body */}
        <div className="flex gap-6 flex-1 min-h-0 items-start">
          {/* Left: numbered steps (compact) */}
          <div className="w-[440px] flex-shrink-0 relative">
            {/* Gradient connector line */}
            <div
              className="absolute left-[27px] top-7 bottom-7 w-[2px] rounded-full"
              style={{
                background:
                  "linear-gradient(to bottom, #3b82f6 0%, #6366f1 30%, #8b5cf6 55%, #a855f7 80%, #d946ef 100%)",
                opacity: 0.5,
              }}
            />

            <div className="space-y-2.5">
              {steps.map((s, i) => {
                const Icon = s.icon;
                const isActive = i === active;
                return (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + i * 0.07, duration: 0.45, ease: "easeOut" }}
                    onClick={() => setActive(i)}
                    className="relative flex items-center gap-4 text-left w-full group"
                  >
                    {/* Number bubble */}
                    <div className="relative flex-shrink-0 w-[56px] flex items-center justify-center">
                      <div
                        className="relative w-[48px] h-[48px] rounded-full flex items-center justify-center font-extrabold text-white text-[19px] ring-4 ring-slate-50 transition-all duration-300"
                        style={
                          isActive
                            ? {
                                background: `linear-gradient(135deg, ${s.color} 0%, ${s.color}dd 100%)`,
                                boxShadow: `0 8px 20px -6px ${s.color}88, 0 0 0 1px ${s.color}40 inset`,
                              }
                            : {
                                background: "#fff",
                                color: s.color,
                                boxShadow: `0 2px 8px -2px rgba(15,23,42,0.12), 0 0 0 1.5px ${s.color}50 inset`,
                              }
                        }
                      >
                        {i + 1}
                      </div>
                    </div>

                    {/* Card */}
                    <div
                      className={`flex-1 bg-white/90 backdrop-blur-sm rounded-xl border px-4 py-3 flex items-center gap-3.5 transition-all duration-300 ${
                        isActive
                          ? "border-slate-200/80 shadow-[0_12px_32px_-12px_rgba(15,23,42,0.18)]"
                          : "border-slate-200/60 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.06)] opacity-70 group-hover:opacity-100 group-hover:shadow-[0_8px_24px_-10px_rgba(15,23,42,0.12)]"
                      }`}
                      style={{ borderLeftWidth: "3px", borderLeftColor: isActive ? s.color : "#e2e8f0" }}
                    >
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-300"
                        style={{
                          background: `linear-gradient(135deg, ${s.color}18, ${s.color}08)`,
                          color: s.color,
                        }}
                      >
                        <Icon className="w-[20px] h-[20px]" strokeWidth={2.3} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[18px] font-bold text-slate-900 leading-tight">
                          {s.title}
                        </h3>
                        <p className="text-[15px] text-slate-500 leading-snug mt-0.5">{s.blurb}</p>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Right: detail */}
          <div className="flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 shadow-[0_12px_32px_-16px_rgba(15,23,42,0.1)] p-6 flex flex-col overflow-hidden self-stretch">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="h-full flex flex-col"
              >
                {/* Detail header */}
                <div className="mb-4 flex-shrink-0">
                  <h3 className="text-[26px] font-extrabold text-slate-900 tracking-tight leading-tight">
                    {step.detailTitle}
                  </h3>

                  {/* Goal callout — clearly visible per-step purpose */}
                  <div
                    className="mt-3 rounded-xl border px-4 py-3.5"
                    style={{
                      background: `linear-gradient(135deg, ${step.color}10, ${step.color}05)`,
                      borderColor: `${step.color}40`,
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div
                        className="w-6 h-6 rounded-md flex items-center justify-center"
                        style={{
                          background: `linear-gradient(135deg, ${step.color}, ${step.color}cc)`,
                        }}
                      >
                        <Target className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                      </div>
                      <span
                        className="text-[11px] font-extrabold uppercase tracking-[0.12em]"
                        style={{ color: step.color }}
                      >
                        The goal
                      </span>
                    </div>
                    <p className="text-[15.5px] text-slate-700 leading-[1.55] font-medium">
                      {step.goal}
                    </p>
                  </div>
                </div>

                {/* Detail body */}
                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  {active === 0 ? (
                    <ReplacementTable color={step.color} />
                  ) : active === 1 ? (
                    <TaskGrid tasks={frontOfficeTasks} color={step.color} />
                  ) : active === 2 ? (
                    <TaskGrid tasks={backOfficeTasks} color={step.color} />
                  ) : (
                    <div className="space-y-4 text-[17.5px] text-slate-600 leading-[1.7] max-w-3xl">
                      {step.body}
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipSlide;
